/**
 * E19-6 (MED-502): Stateful attendance follow-up banner for the Participants tab.
 *
 * - Pending state: orange icon circle, "N need attention . M have been addressed"
 * - All addressed state: green icon circle, "All follow-ups addressed . N total"
 * - Hidden when no follow-ups have ever been generated
 */

import { Button, Group, Text, Box } from '@mantine/core';
import { AlertTriangle, CheckCircle } from 'react-feather';
import { useFollowUpSummary } from '../hooks/useFollowUpSummary';

interface AttendanceFollowUpBannerProps {
  cohortId: string;
  onOpenFollowUps: (options?: { sessionId?: string; participantId?: string }) => void;
}

export function AttendanceFollowUpBanner({ cohortId, onOpenFollowUps }: AttendanceFollowUpBannerProps) {
  const { summary } = useFollowUpSummary({ cohortId });

  // Don't render if no follow-ups have ever been generated
  if (!summary || summary.total_ever_generated === 0) return null;

  const hasPending = summary.total_pending > 0;

  return (
    <Box
      style={{
        background: 'white',
        border: '1px solid #E8E6E1',
        borderRadius: 8,
        padding: '14px 18px',
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="md" align="center" wrap="nowrap">
          {/* Status icon circle */}
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hasPending ? '#FEF3E7' : 'rgb(232, 245, 233)',
              flexShrink: 0,
            }}
          >
            {hasPending ? (
              <AlertTriangle size={18} color="#E9A23B" />
            ) : (
              <CheckCircle size={18} color="#2D4739" />
            )}
          </Box>

          {/* Text content */}
          <div>
            <Text fw={600} size="sm" c="#1A1A1A">
              Attendance Follow Ups
            </Text>
            <Text size="xs" c="#666666">
              {hasPending
                ? `${summary.total_pending} need attention \u00B7 ${summary.total_addressed} have been addressed`
                : `All follow-ups addressed \u00B7 ${summary.total_ever_generated} total`}
            </Text>
          </div>
        </Group>

        <Button
          size="xs"
          variant="filled"
          color="medleyGreen"
          onClick={() => onOpenFollowUps()}
          style={{ flexShrink: 0 }}
        >
          View Attendance Follow Ups
        </Button>
      </Group>
    </Box>
  );
}
